import { Box, Paper } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { postWorkbenchPages } from 'apis/workbench';
import { useUser } from 'contexts/user-context';
import { FC, useCallback, useState } from 'react';
import { ColOrderType } from 'types/types';
import { convertQueryBuilderTree } from 'utils/utils-query-builder';
import WBPageTable from './WBPageTable';
import { StateProp as OperationStateProp } from 'components/workbench/OperationBuilder';
import WBExportBtn from '../WBExportBtn';
import ActionSelector from '../ActionSelector';
import useWBActions from 'hooks/useWBActions';
import useWBCols from 'hooks/useWBCols';
import WBQBRecipe from '../WBQBRecipe';
import FieldSetupDlg from '../dialog/FieldSetupDlg';
import SetupColBtn from '../SetupColBtn';
import { initTableDataFromDataRaw } from 'utils';

type Props = {
  opporId?: number;
};

const WBPage: FC<Props> = ({ opporId }) => {
  const { curIndex } = useUser();
  const [page, setPage] = useState(0);
  const [offset, setOffset] = useState(10);
  const [backQuery, setBackQuery] = useState<any>(null);
  const [operation, setOperation] = useState<OperationStateProp>();
  const [order, setOrder] = useState<ColOrderType>({
    name: 'status_code',
    direction: 'asc',
  });
  let { data: dataRaw, isLoading } = useQuery(
    [
      curIndex,
      'WB',
      'page',
      page,
      offset,
      backQuery,
      order,
      operation,
      opporId,
    ],
    () =>
      postWorkbenchPages(curIndex!.value, {
        page,
        offset,
        operation: operation?.operations[0],
        back_query: backQuery,
        order_column: order.name,
        order_dir: order.direction,
        opporId,
      }),
    {
      enabled: Boolean(curIndex),
      keepPreviousData: true,
    }
  );
  const handleChangeRecipe = useCallback((jsonTree: any) => {
    const _backQuery = convertQueryBuilderTree(jsonTree);
    setBackQuery(_backQuery);
  }, []);

  const handleOperationChange = useCallback((operation: OperationStateProp) => {
    setOperation(operation);
  }, []);

  const {
    invisibleCols,
    setInvisibleCols,
    colsDisplayOrder,
    setColsDisplayOrder,
    colsWidth,
    setColsWidth,
    columns,
  } = useWBCols({ source: 'page' });

  const data = initTableDataFromDataRaw(dataRaw);
  data['items'] = data.items.map((item: any) => ({
    ...item,
    __operation: operation,
  }));

  const {
    handleSelectAction,
    fieldOptions,
    options2,
    groupOptions,
    isSupportRowSelector,
  } = useWBActions({
    source: 'page',
  });

  const [isFieldSetupDlgOpen, setIsFieldSetupDlgOpen] = useState(false);

  return (
    <Box sx={{ ml: 3, mr: 3, mt: 4 }}>
      {isFieldSetupDlgOpen && (
        <FieldSetupDlg
          open={isFieldSetupDlgOpen}
          onClose={() => setIsFieldSetupDlgOpen(false)}
          source="page"
        />
      )}
      <Box mt={4}>
        <WBQBRecipe
          onRecipeChange={handleChangeRecipe}
          onOperationChange={handleOperationChange}
          col_sorting={order}
          col_invisible={invisibleCols.cols}
          setOrder={setOrder}
          setInvisibleCols={setInvisibleCols}
          col_display_order={colsDisplayOrder}
          col_width={colsWidth}
          setColsDisplayOrder={setColsDisplayOrder}
          setColsWidth={setColsWidth}
          source={'page'}
          tableData={data}
        />
      </Box>
      <Paper sx={{ mt: 4 }}>
        <ActionSelector
          fieldOptions={fieldOptions}
          isSupportRowSelector={isSupportRowSelector}
          options2={options2}
          backQuery={backQuery}
          source="page"
          onSelectOption={handleSelectAction}
          opporId={opporId}
          groupOptions={groupOptions}
        />
        <Box
          sx={{
            m: 1,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <WBExportBtn source="page" back_query={backQuery} opporId={opporId} />
          <SetupColBtn onClick={() => setIsFieldSetupDlgOpen(true)} />
        </Box>
        <WBPageTable
          data={data}
          isLoading={isLoading}
          page={page}
          setPage={setPage}
          setOrder={setOrder}
          offset={offset}
          setOffset={setOffset}
          setColsDisplayOrder={setColsDisplayOrder}
          setColsWidth={setColsWidth}
          columns={columns}
        />
      </Paper>
    </Box>
  );
};

export default WBPage;
